
import BaseAlert from "@/components/base/BaseAlert.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseExpansion from "@/components/base/BaseExpansion.vue";
import BaseSvg from "@/components/base/BaseSvg.vue";
import useAmendment from "@/composables/useAmendment";
import useBooking from "@/composables/useBooking";
import { EEEddMMMYYYY, getDurationInMinutes, HHmm } from "@/helpers/dateHelper";
import { ConfirmationEmailRequest } from "@/models/api/ConfirmationEmailRequest";
import { AmendmentOptionTypeEnum } from "@/models/enums";
import { amendmentDialogKey, AmendmentOptions } from "@/models/symbols/Amendment";
import { CancelBooking, cancelDialogKey } from "@/models/symbols/CancelBooking";
import { ConfirmationEmail, confirmationEmailDialogKey } from "@/models/symbols/ConfirmationEmail";
import { BookingSummary } from "@/models/www/store/BookingSummary";
import { useEventBus } from "@vueuse/core";
import { defineComponent, PropType, ref } from "vue";

export default defineComponent ({
	components: {
		BaseSvg,
        BaseButton,
        BaseExpansion,
        BaseAlert
	},
    props: {
        booking: {
            type: Object as PropType<BookingSummary>,
            required: true
        },
        isAgent: {
        		type: Boolean,
        		required: false
        },
    },
    setup(props) {
        const { 
            startAmendment, 
            prepareBookingCancellation,
            prepareDepartureCancellation
        } = useAmendment();
        const { sendConfirmationMail } = useBooking();
        const expanded = ref<boolean>(false);
        const amendmentBus = useEventBus<AmendmentOptions>(amendmentDialogKey);
        const cancelBus = useEventBus<CancelBooking>(cancelDialogKey);
        const confirmationBus = useEventBus<ConfirmationEmail>(confirmationEmailDialogKey);
				
        const loading = ref<boolean>(false);
        const loadingCancel = ref<boolean>(false);

        const expand = () => {
            expanded.value = !expanded.value;
        }
        
        const prepareAmendment = (step: number, amendmentType: AmendmentOptionTypeEnum) => {
            return new Promise((resolve, reject) => {
                if(props.booking.bookingReference && amendmentType === AmendmentOptionTypeEnum.Edit) {
                    startAmendment(step, props.booking.bookingReference)
                    .then(() => resolve(""))
                    .catch((err) => reject(err));
                }
                else {
                    prepareCancellation(amendmentType)
                    .then(() => resolve(""))
                    .catch((err) => reject(err));
                }
            });
        }

        const goToCheckin = (url: string) => {
            window.location.replace(url);
        }

        const updateBooking = () => {
            amendmentBus.emit({
                callback: (step: number, amendmentType: AmendmentOptionTypeEnum) => prepareAmendment(step, amendmentType),
                isEditable: props.booking.isEditable,
                booking: props.booking
            });
        }

        const resendConfirmation = () => {
            if(props.booking.bookingReference) {
                confirmationBus.emit({
                    callback: (email: string, pricedConfirmation: boolean) => sendConfirmationMail({
                            id: props.booking.bookingReference as string,
                            email: email,
                            pricedConfirmation: pricedConfirmation 
                        } as ConfirmationEmailRequest)
                });       
            }
        }

        const prepareCancellation = (type: AmendmentOptionTypeEnum): Promise<void> => {
            if(props.booking.bookingReference) {
                switch(type) {
                    case AmendmentOptionTypeEnum.CancelOutward:
                    case AmendmentOptionTypeEnum.CancelReturn: {
                        const route = type === AmendmentOptionTypeEnum.CancelOutward ? 
                                                props.booking.bookingSections[0].route :
                                                props.booking.bookingSections[1].route;

                        return prepareDepartureCancellation(route, props.booking.bookingReference)
                        .then(resp => {                  
                            cancelBus.emit({                        
                                booking: props.booking,
                                payment: resp,
                                type: type,
                                cancelRoute: route
                            })
                        });                      
                    }                 
                    
                    case AmendmentOptionTypeEnum.CancelBooking: {
                        loadingCancel.value = true;

                        return prepareBookingCancellation(props.booking.bookingReference)
                        .then(resp => {
                            cancelBus.emit({                            
                                booking: props.booking,
                                payment: resp,
                                type: type
                            })
                        }).finally(() => loadingCancel.value = false);
                    }              
                }
            }

            return new Promise((resolve, reject) => {
                reject("Booking reference not set");
            });
        }

        return { 
            HHmm, 
            expand, 
            expanded, 
            getDurationInMinutes, 
            EEEddMMMYYYY,
            updateBooking,
            prepareCancellation,
            resendConfirmation,
            loading,
            loadingCancel,
            AmendmentOptionTypeEnum,
            goToCheckin
        }
    }
})
